import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { ShareLevelPicker } from '../profile/ShareLevelPicker';
import { logWarn } from '../../services/loggerService';

interface FirstPublishVisibilityModalProps {
  isOpen: boolean;
  /** Pre-selected tier (the chef's current profile default, or 3). */
  defaultLevel: number;
  /** Disables the controls while the choice is being persisted + published. */
  isSubmitting?: boolean;
  onConfirm: (level: number) => void;
  onClose: () => void;
  /**
   * Locale cap from `Locale.maxShareConnectionLevel`. Forwarded to
   * `ShareLevelPicker` so options above the cap aren't offered, AND
   * applied to the modal's own initial level state so the prompt opens
   * at a value the chef can actually save (it's a fresh first-time pick;
   * no over-cap legacy to preserve here).
   * Default 3 = no narrowing.
   */
  maxLevel?: number;
}

/**
 * Shown the first time a chef publishes: asks for their default listing
 * visibility, which we persist as their profile default before completing the
 * publish. Reuses the shared ShareLevelPicker so the options match the Profile
 * setting exactly.
 */
export const FirstPublishVisibilityModal: React.FC<FirstPublishVisibilityModalProps> = ({
  isOpen,
  defaultLevel,
  isSubmitting,
  onConfirm,
  onClose,
  maxLevel = 3,
}) => {
  const { t } = useTranslation('publish');
  // Clamp the seed to the locale cap — this is a first-publish pick, so the
  // chef has no prior over-cap setting to preserve. Picking a value above
  // the cap would just get clamped server-side by `LEAST(...)` anyway.
  //
  // Defensive clamp: valid shareConnectionLevel values are [1..3]. If the
  // locale reports 0 / negative for maxShareConnectionLevel (BE data or
  // serialization bug), Math.min(defaultLevel, 0) collapses to 0 and the
  // Confirm handler would PATCH `shareConnectionLevel: 0`, which the BE
  // rejects with a 400. Treat any sub-1 cap as "unlimited (3)" here.
  const effectiveMax = maxLevel >= 1 ? maxLevel : 3;
  const initialLevel = Math.max(1, Math.min(defaultLevel, effectiveMax));
  const [level, setLevel] = useState(initialLevel);

  useEffect(() => {
    if (isOpen) setLevel(Math.max(1, Math.min(defaultLevel, effectiveMax)));
  }, [isOpen, defaultLevel, effectiveMax]);

  // Diagnostic: the clamp above silently masks a bad locale payload, so
  // surface it here so we don't lose the signal — the real fix belongs
  // upstream (BE-side serialization or the locale record itself).
  useEffect(() => {
    if (isOpen && maxLevel < 1) {
      logWarn(
        'FirstPublishVisibilityModal: locale returned maxShareConnectionLevel < 1 — clamping to 3',
        undefined,
        { maxLevel, defaultLevel },
      );
    }
  }, [isOpen, maxLevel, defaultLevel]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" zIndexClass="z-[70]">
      <h2 className="mb-2 text-lg font-bold text-foreground">
        {t('firstVisibility.title')}
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">{t('firstVisibility.body')}</p>
      <ShareLevelPicker value={level} onChange={setLevel} disabled={isSubmitting} maxLevel={effectiveMax} />
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onConfirm(level)}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
        >
          {t('firstVisibility.confirm')}
        </button>
      </div>
    </Modal>
  );
};
